import type { SxProps, Theme } from '@mui/material';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  MenuList,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useLocation } from '@remix-run/react';

import { GearSettings, Hierarchy, Logout } from '@tbd/life-jsx/SystemIcons';
import { BOXSHADOWS, COLORS } from '@tbd/life-tokens';
import type { RoleName } from '@tbd/lift-portal-shared-config';
import { ROLE_NAMES } from '@tbd/lift-portal-shared-config';
import {
  TextButton,
  textButtonClasses,
} from '@tbd/lift-ui/components/TextButton';
import { TypographyClampLines } from '@tbd/lift-ui/components/TypographyClampLines';

const BADGE_SIZE_SMALL = '36px';
const BADGE_MARGIN = '4px';

type AuthenticatedUser = {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  email: string | null | undefined;
  role: RoleName;
};

type ProfileBadgeProps = {
  user: AuthenticatedUser;
  avatarStyles?: SxProps<Theme>;
  typographyStyles?: SxProps<Theme>;
};

// When ProfileMenu is implemented in GlobalNavigationBar, remove this export statement
export function ProfileBadge(props: ProfileBadgeProps) {
  return (
    <Avatar
      sx={{
        width: BADGE_SIZE_SMALL,
        height: BADGE_SIZE_SMALL,
        margin: BADGE_MARGIN,
        backgroundColor: COLORS.neutral_050,
        color: COLORS.black,
        ...(props.avatarStyles || {}),
      }}
    >
      <Typography
        variant="component-bold-xs"
        sx={{
          textTransform: 'uppercase',
          ...(props.typographyStyles || {}),
        }}
      >
        {props.user.firstName ? props.user.firstName.toString()[0] : null}
        {props.user.lastName ? props.user.lastName.toString()[0] : null}
      </Typography>
    </Avatar>
  );
}

export type ProfileMenuProps = {
  user: AuthenticatedUser;
  portalOrigin: string;
  groupSelectionHref?: string;
  projectSelectionHref?: string;
  currentProjectName?: string;
};

function ProfileMenuItemButton(props: {
  href: string;
  icon: React.ReactNode;
  text: string;
}) {
  return (
    <TextButton
      href={props.href}
      variant="tertiary"
      size="medium"
      sx={{
        justifyContent: 'flex-start',
        width: '100%',
        lineHeight: '48px',
        padding: '12px 8px',
        borderRadius: '6px',
        color: COLORS.neutral_700,
        ['&:hover']: {
          color: COLORS.accent1_600,
        },
        // TextButton (really MenuButton) adds margin to the icon, so we need to remove it
        [`&.${textButtonClasses.sizeMedium} > .${textButtonClasses.icon}`]: {
          marginLeft: '-2px',
        },
        [`& .${textButtonClasses.startIcon}`]: {
          width: '16px',
          marginRight: '10px', // 8px padding + 2px to compensate for the -2px margin above
        },
        // keep size/weight as-is but should probably be a variant instead
        '&': {
          height: '44px', // 20 + (12x2)
          fontSize: '16px',
          fontWeight: 400,
        },
      }}
      icon={props.icon}
      text={props.text}
    />
  );
}

export function ProfileMenu(props: ProfileMenuProps) {
  const location = useLocation();
  const theme = useTheme();
  const user = props.user;
  const fullDisplayName =
    `${user.firstName?.toString()} ${user.lastName?.toString()}`.trim();

  const userMenuId = 'menu-user';

  return (
    <Box
      key={location.key}
      component="details"
      sx={{
        position: 'relative',
        padding: '0',
        border: 'none',
        /* When the menu is open, add the (transparent) backdrop which we can use to "catch" clicks outside the menu */
        '&[open] > summary::before': {
          content: "' '",
          display: 'block',
          position: 'fixed',
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
        },
      }}
    >
      <IconButton
        component="summary"
        aria-label="Profile Information menu"
        aria-controls={userMenuId}
        aria-haspopup="true"
        sx={{
          /* Hide the backdrop by default */
          '&::before': { display: 'none' },

          listStyle: 'none',
          listStyleType: 'none',
          '&::-webkit-details-marker': {
            display: 'none',
          },
          '&:focus': {
            outline: 'none',
          },
          padding: 0,
        }}
      >
        <ProfileBadge user={user} />
      </IconButton>
      <MenuList
        component="ul"
        id={userMenuId}
        sx={{
          '--badge-size': `calc(${BADGE_SIZE_SMALL} + ${BADGE_MARGIN})`,
          '--menu-offset': '2px',
          '--menu-width': '320px',

          position: 'absolute',
          top: `calc(var(--icon-top-margin) + var(--badge-size) + var(--menu-offset))`,
          padding: '8px 0',
          width: 'var(--menu-width)',
          marginLeft: 'calc( calc(var(--menu-width) - var(--badge-size)) * -1)',
          boxSizing: 'border-box',
          zIndex: '2',
          background: 'white',
          borderRadius: '6px',
          listStyle: 'none',
          boxShadow: BOXSHADOWS.BOXSHADOW_CENTERED,
        }}
      >
        <Stack
          key="profile-row"
          component="li"
          role="menuitem"
          sx={{
            padding: '0 8px',
          }}
        >
          <Stack
            direction="row"
            sx={{
              padding: '6px 8px 16px 8px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <Box
              sx={{
                padding: '2px 8px 2px 0px',
              }}
            >
              <ProfileBadge
                user={user}
                typographyStyles={theme.typography['component-regular-xs']}
              />
            </Box>
            <Stack
              direction="row"
              sx={{
                flexGrow: 1,
              }}
            >
              <Stack
                sx={{
                  flexGrow: 1,
                }}
              >
                <Typography
                  color={COLORS.neutral_800}
                  variant="component-regular-s"
                >
                  {fullDisplayName}
                </Typography>
                <TypographyClampLines
                  color={COLORS.neutral_500}
                  variant="component-regular-s"
                  lines={1}
                >
                  {user?.email
                    ? user.email
                    : `${user?.firstName} ${user?.lastName}`}
                </TypographyClampLines>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Divider variant="fullWidth" />
        <Stack
          sx={{
            padding: '8px 8px 0 8px',
          }}
        >
          {props.projectSelectionHref ? (
            <Stack key="project-switcher" component="li" role="menuitem">
              {props.currentProjectName ? (
                <Typography
                  variant="component-bold-s"
                  color={COLORS.neutral_700}
                >
                  {props.currentProjectName}
                </Typography>
              ) : null}
              <ProfileMenuItemButton
                href={props.projectSelectionHref}
                text="Switch project"
                icon={<Hierarchy />}
              />
            </Stack>
          ) : null}
          {props.groupSelectionHref ? (
            <Stack key="group-switcher" component="li" role="menuitem">
              <ProfileMenuItemButton
                href={props.groupSelectionHref}
                text="Switch group"
                icon={<Hierarchy />}
              />
            </Stack>
          ) : null}
          <Stack key="logout-row" component="li" role="menuitem">
            {user.role === ROLE_NAMES.LGH_SUPPORT_STAFF ? (
              <ProfileMenuItemButton
                href={`${props.portalOrigin}/admin/`}
                icon={<GearSettings />}
                text={'LGH Administration'}
              />
            ) : null}
            <ProfileMenuItemButton
              href={`${props.portalOrigin}/logout`}
              icon={<Logout />}
              text={'Sign out'}
            />
          </Stack>
        </Stack>
      </MenuList>
    </Box>
  );
}
